import { PropertyCharacteristics, PropertyType } from '@shared/definitions/property';
import { isBareRoom } from '@shared/definitions/room-composition.util';
import type { PropertyQualityScore, PropertySectionScore } from '@core/services/property-analysis/property-scoring.types';

/**
 * Fields the scoring logic reads but that aren't part of the canonical
 * `PropertyCharacteristics` interface yet. Kept as an intersection so the
 * scoring service has a single typed view instead of scattering `as any` casts.
 *
 * When these fields land on `PropertyCharacteristics` proper, just delete the
 * entry here — the intersection is additive.
 */
export type ScoringCharacteristics = PropertyCharacteristics & {
  openWorkspaces?: number | null;
  hasReception?: boolean | null;
  hasFiberInternet?: boolean | null;
  networkInfrastructure?: string | boolean | null;
  hasKitchenArea?: boolean | null;
  warehouseSections?: number | null;
  hasFireSuppression?: boolean | null;
  hasDisplayWindows?: boolean | null;
  displayWindows?: number | null;
  hasPOSSystem?: boolean | null;
  numberOfPOSTerminals?: number | null;
  frontageWidth?: number | null;
  hasAccessRoad?: boolean | null;
  roadAccess?: boolean | null;
  hasWaterConnection?: boolean | null;
  hasElectricityConnection?: boolean | null;
  hasSewerConnection?: boolean | null;
  hasElevator?: boolean | null;
  hasFencedPerimeter?: boolean | null;
  hasSecurityPerimeter?: boolean | null;
  streetFrontage?: number | null;
  hasLoadingAccess?: boolean | null;
  hasDeliveryAccess?: boolean | null;
};

/**
 * Équipements spéciaux : les champs de `characteristics` que l'éditeur
 * d'équipement (`edit-single-feature-dialog`) sait écrire. Le garage et le
 * jardin ont leur propre critère et n'en font pas partie.
 *
 * Source unique : le scoring les compte, et le sélecteur de « Mise en route du
 * bien » ne propose que les équipements qui aboutissent à l'un de ces champs.
 */
export const SPECIAL_FEATURE_FIELDS = [
  'poolDetails',
  'gymDetails',
  'saunaDetails',
  'homeTheaterDetails',
  'wineCellarDetails',
  'securityDetails',
  'laundryDetails',
  'officeDetails',
  'bbqDetails',
  'playgroundDetails',
  'tennisCourtDetails',
  'solarPanelsDetails',
  'evChargingDetails',
  'smartHomeDetails'
] as const;

/** Nombre d'équipements spéciaux renseignés (valeur non nulle, même vide : « présent »). */
export const countSpecialFeatures = (characteristics: PropertyCharacteristics): number =>
  SPECIAL_FEATURE_FIELDS.filter(field => {
    const value = (characteristics as unknown as Record<string, unknown>)[field];
    return value !== null && value !== undefined;
  }).length;

/**
 * Composition détaillée : au moins une pièce qui porte autre chose que son type.
 * Saisir « 2 chambres » crée deux pièces VIDES (nom vide, pas de surface) : elles
 * disent combien, pas ce que c'est, et ne comptent donc pas comme composition.
 */
export const hasDetailedComposition = (characteristics: PropertyCharacteristics): boolean =>
  (characteristics.roomDetails ?? []).some(room => !isBareRoom(room));

/**
 * Balcon ou terrasse : la liste dédiée, une pièce de ce type, ou un compteur.
 * La liste `balconyTerraceDetails` n'est écrite par aucun écran — les balcons se
 * déclarent dans la composition — d'où les deux autres sources.
 */
export const hasBalconyOrTerrace = (characteristics: PropertyCharacteristics): boolean => {
  if ((characteristics.balconyTerraceDetails ?? []).length > 0) return true;
  if ((characteristics.roomDetails ?? []).some(r => ['balcony', 'terrace', 'balcony_terrace'].includes(r.roomType))) return true;
  return (characteristics.balconies ?? 0) > 0 || (characteristics.terraces ?? 0) > 0;
};

const RESIDENTIAL_TYPES: ReadonlySet<PropertyType> = new Set([
  PropertyType.Apartment,
  PropertyType.House,
  PropertyType.Villa
]);

const COMMERCIAL_TYPES: ReadonlySet<PropertyType> = new Set([
  PropertyType.Office,
  PropertyType.Retail,
  PropertyType.RetailWarehouse,
  PropertyType.Warehouse
]);

const INDUSTRIAL_TYPES: ReadonlySet<PropertyType> = new Set([
  PropertyType.Warehouse,
  PropertyType.RetailWarehouse
]);

export function isResidential(type: PropertyType): boolean {
  return RESIDENTIAL_TYPES.has(type);
}

export function isCommercial(type: PropertyType): boolean {
  return COMMERCIAL_TYPES.has(type);
}

export function isIndustrial(type: PropertyType): boolean {
  return INDUSTRIAL_TYPES.has(type);
}

export function isLand(type: PropertyType): boolean {
  return type === PropertyType.Land;
}

export type QualityLevel = 'excellent' | 'good' | 'fair' | 'poor';

/**
 * Quality bands for the overall percentage score.
 * Threshold = lower-bound (inclusive). Sorted high → low so the first match wins.
 */
const QUALITY_BANDS: readonly { threshold: number; level: QualityLevel; description: string }[] = [
  {
    threshold: 85,
    level: 'excellent',
    description:
      'Excellent listing quality. Ready for premium marketplace visibility on Immoweb and similar platforms.'
  },
  {
    threshold: 70,
    level: 'good',
    description:
      'Good listing quality. Suitable for marketplace listing with good visibility potential.'
  },
  {
    threshold: 50,
    level: 'fair',
    description:
      'Fair listing quality. Consider adding missing information to improve marketplace visibility.'
  },
  {
    threshold: 0,
    level: 'poor',
    description:
      'Limited listing quality. Significant information gaps may reduce marketplace visibility. Please complete missing fields.'
  }
];

export function getQualityLevel(percentage: number): QualityLevel {
  return (QUALITY_BANDS.find(b => percentage >= b.threshold) ?? QUALITY_BANDS[QUALITY_BANDS.length - 1]).level;
}

export function getMarketReadinessDescription(percentage: number): string {
  return (QUALITY_BANDS.find(b => percentage >= b.threshold) ?? QUALITY_BANDS[QUALITY_BANDS.length - 1])
    .description;
}

export function getScoreTooltip(score: PropertyQualityScore): string {
  return (
    `Listing Quality Score: ${score.overallPercentage}%\n\n` +
    `This score measures data completeness for marketplace listings like Immoweb. ` +
    `Higher scores indicate better visibility potential.\n\n` +
    `Quality Level: ${score.qualityLevel.toUpperCase()}\n\n` +
    `${score.marketReadiness}`
  );
}

export function getSectionTooltip(section: PropertySectionScore): string {
  const completed = section.completedFields.length;
  const total = completed + section.missingFields.length;

  return (
    `${section.section}: ${section.score}/${section.maxScore} points (${section.percentage}%)\n\n` +
    `Completed: ${completed}/${total} items\n\n` +
    (section.missingFields.length > 0
      ? `Missing:\n• ${section.missingFields.join('\n• ')}`
      : 'All fields completed!')
  );
}
